import React from "react";
import PropTypes from "prop-types";
import {
    Button,
    Dropdown,
    Input,
    Menu,
    Modal,
    Select,
    Tooltip,
    message
} from "antd";
import { CompanyService } from "services/index";
import { fiMovementAction } from "services/case-service";

// FI Movement actions — Tag Existing Case / Create New Case / Balance Transfer
// Case / Un-tag Case. Shared by the standalone All Cases tab
// (previous-cases-list.js) and the Credit Checker "Existing Relationship →
// All Cases" grid (credit-checker/components/portfolio-cases-table.js) so the
// business rules (payload shape, eligibility flags) live in exactly one place.
//
// Row shape expected (present on both consumers' rows, straight off the same
// case-list API): id, bank {id, code, name}, proposed_or_sanction_limit,
// can_be_tagged_as_fi_movement, can_be_parent_of_fi_movement,
// can_be_untagged_as_fi_movement. `caseId` / `current_stage` (display strings)
// are read with a fallback to the raw API field names since the two callers
// don't rename them identically.

const MODALS = {
    TAG: "showTagExistingCaseModal",
    CREATE: "showCreateNewCaseModal",
    BALANCE_TRANSFER: "showBalanceTransferCaseModal",
    UNTAG: "showUntagCaseModal"
};

// `page_all_cases_fi_movement_action` gates every action here. The context is an
// Immutable map on the case-details tabs, but read defensively so a plain object
// (or a missing context) doesn't throw.
export function canActionFiMovement(permissionContext) {
    if (!permissionContext) {
        return false;
    }
    if (typeof permissionContext.get === "function") {
        return Boolean(
            permissionContext.get("page_all_cases_fi_movement_action")
        );
    }
    return Boolean(permissionContext.page_all_cases_fi_movement_action);
}

// Only live cases can take part in an FI movement — same statuses the All Cases
// tab allows a checkbox on.
export const FI_MOVEMENT_SELECTABLE_STATUSES = ["Active", "Open"];

function toAmount(value) {
    const n = Number(String(value == null ? "" : value).replace(/[^0-9.-]/g, ""));
    return Number.isFinite(n) ? n : 0;
}

function displayStage(row) {
    return (row && (row.current_stage || row.current_stage_display)) || "—";
}

class FiMovementBar extends React.Component {
    state = {
        modal: null,
        submitting: false,
        lendingPartners: [],
        existingCaseId: null,
        partnerBankId: null,
        proposedLimit: null
    };

    // Every selected row must be eligible for the action — matches
    // `parentFiMovement` on the All Cases tab.
    everyRowAllows(flag) {
        const rows = this.props.selectedRows || [];
        return rows.length > 0 && rows.every(row => Boolean(row[flag]));
    }

    openModal = modal => {
        // Seed the proposed limit with the sum of the selected cases' limits —
        // the reviewer can overwrite it in the modal.
        const proposedLimit = (this.props.selectedRows || []).reduce(
            (sum, row) => sum + toAmount(row.proposed_or_sanction_limit),
            0
        );
        this.setState({ modal, proposedLimit: proposedLimit || null });
        CompanyService.getLendingPartners()
            .then(({ data }) =>
                this.setState({ lendingPartners: data || [] })
            )
            .catch(() => this.setState({ lendingPartners: [] }));
    };

    closeModal = () =>
        this.setState({
            modal: null,
            existingCaseId: null,
            partnerBankId: null,
            proposedLimit: null
        });

    // Cases that can receive this movement: every other case in the grid that
    // the backend marks taggable, minus the ones already selected as parents.
    taggableCases() {
        const selectedIds = (this.props.selectedRows || []).map(row => row.id);
        return (this.props.rows || []).filter(
            row =>
                row.can_be_tagged_as_fi_movement &&
                selectedIds.indexOf(row.id) === -1
        );
    }

    // Partner banks the selected cases aren't already with — a movement to the
    // same FI partner is a no-op.
    availablePartners() {
        const selectedBankIds = (this.props.selectedRows || [])
            .map(row => row.bank && row.bank.id)
            .filter(id => id != null);
        return (this.state.lendingPartners || []).filter(
            partner => selectedBankIds.indexOf(partner.id) === -1
        );
    }

    buildPayload(actionType) {
        const rows = this.props.selectedRows || [];
        const parentIds = rows.map(row => row.id);
        if (actionType === "tag") {
            if (!this.state.existingCaseId) {
                message.error("No existing case selected");
                return null;
            }
            return {
                fi_movement_parent_case_ids: parentIds,
                case_id: this.state.existingCaseId,
                action_type: "tag"
            };
        }
        if (actionType === "untag") {
            return {
                case_id: parentIds[0],
                action_type: "untag"
            };
        }
        // create / balance_transfer share the same shape.
        const proposedLimit = toAmount(this.state.proposedLimit);
        if (!(proposedLimit > 0)) {
            message.error("Please enter Proposed limit > 0");
            return null;
        }
        const payload = {
            fi_movement_parent_case_ids: parentIds,
            proposed_limit: proposedLimit,
            partner_bank_id: this.state.partnerBankId,
            action_type: actionType
        };
        if (actionType === "balance_transfer") {
            payload.is_balance_transfer_case = true;
        }
        return payload;
    }

    submit = actionType => {
        const body = this.buildPayload(actionType);
        if (!body) {
            return;
        }
        this.setState({ submitting: true });
        fiMovementAction(body)
            .then(res => {
                message.success(
                    (res && res.data && res.data.message) ||
                        "FI movement updated"
                );
                this.closeModal();
                this.setState({ submitting: false });
                if (this.props.onDone) {
                    this.props.onDone();
                }
            })
            .catch(err => {
                const apiError =
                    err &&
                    err.non_field_errors &&
                    err.non_field_errors.length &&
                    err.non_field_errors[0];
                message.error(apiError || "Something went wrong!");
                this.setState({ submitting: false });
            });
    };

    menu = () => (
        <Menu>
            <Menu.Item
                key={"tagExistingCase"}
                onClick={() => this.openModal(MODALS.TAG)}
            >
                Tag Existing Case
            </Menu.Item>
            <Menu.Item
                key={"createNewCase"}
                onClick={() => this.openModal(MODALS.CREATE)}
            >
                Create New Case
            </Menu.Item>
            <Menu.Item
                key={"balanceTransferCase"}
                onClick={() => this.openModal(MODALS.BALANCE_TRANSFER)}
            >
                Balance Transfer Case
            </Menu.Item>
        </Menu>
    );

    // Partner bank + proposed limit — the Create New Case and Balance Transfer
    // modals collect exactly the same two fields.
    renderLimitForm() {
        return (
            <div className={"cc-fimove-form"}>
                <div className={"cc-fimove-field"}>
                    <p className={"cc-fimove-label"}>Select Partner Bank</p>
                    <Select
                        style={{ width: "100%" }}
                        placeholder={"Select Partner Bank"}
                        value={this.state.partnerBankId || undefined}
                        onSelect={partnerBankId =>
                            this.setState({ partnerBankId })
                        }
                        getPopupContainer={trigger => trigger.parentNode}
                    >
                        {this.availablePartners().map(partner => (
                            <Select.Option key={partner.id} value={partner.id}>
                                {partner.name}
                            </Select.Option>
                        ))}
                    </Select>
                </div>
                <div className={"cc-fimove-field"}>
                    <p className={"cc-fimove-label"}>Proposed Limit</p>
                    <Input
                        type={"text"}
                        placeholder={"Enter proposed limit"}
                        value={
                            this.state.proposedLimit == null
                                ? ""
                                : this.state.proposedLimit
                        }
                        onChange={e =>
                            this.setState({ proposedLimit: e.target.value })
                        }
                    />
                </div>
            </div>
        );
    }

    render() {
        const { selectedRows, recreditViewOnlyPermission } = this.props;
        const selected = selectedRows || [];
        const { modal, submitting } = this.state;
        const canTagOrCreate =
            !recreditViewOnlyPermission &&
            selected.length >= 1 &&
            this.everyRowAllows("can_be_parent_of_fi_movement");
        const canUntag =
            !recreditViewOnlyPermission &&
            selected.length === 1 &&
            this.everyRowAllows("can_be_untagged_as_fi_movement");

        return (
            <span className={"cc-fimove"}>
                <span className={"cc-fimove__label"}>FI Movement</span>
                <Tooltip
                    title={canTagOrCreate ? "" : "Select valid case(s)"}
                >
                    <span>
                        <Dropdown.Button
                            size={"small"}
                            type={"primary"}
                            disabled={!canTagOrCreate}
                            overlay={this.menu}
                        >
                            FI Movement: Tag / Create Case
                        </Dropdown.Button>
                    </span>
                </Tooltip>
                <Tooltip title={canUntag ? "" : "Select valid case(s)"}>
                    <span>
                        <Button
                            size={"small"}
                            type={"primary"}
                            disabled={!canUntag}
                            onClick={() => this.openModal(MODALS.UNTAG)}
                        >
                            FI Movement: Un-tag Case
                        </Button>
                    </span>
                </Tooltip>

                <Modal
                    visible={modal === MODALS.TAG}
                    title={"Select Existing Case"}
                    okText={"Submit"}
                    confirmLoading={submitting}
                    onOk={() => this.submit("tag")}
                    onCancel={this.closeModal}
                >
                    <p className={"cc-fimove-label"}>Select Existing Case</p>
                    <Select
                        style={{ width: "100%" }}
                        placeholder={"Select Existing Case"}
                        value={this.state.existingCaseId || undefined}
                        onSelect={existingCaseId =>
                            this.setState({ existingCaseId })
                        }
                        getPopupContainer={trigger => trigger.parentNode}
                    >
                        {this.taggableCases().map(row => (
                            <Select.Option key={row.id} value={row.id}>
                                {`${row.id} | ${(row.bank && row.bank.code) ||
                                    row.fi_partner ||
                                    "—"} | ${displayStage(row)}`}
                            </Select.Option>
                        ))}
                    </Select>
                </Modal>

                <Modal
                    visible={modal === MODALS.CREATE}
                    title={"Create New Case"}
                    okText={"Submit"}
                    confirmLoading={submitting}
                    onOk={() => this.submit("create")}
                    onCancel={this.closeModal}
                >
                    {this.renderLimitForm()}
                </Modal>

                <Modal
                    visible={modal === MODALS.BALANCE_TRANSFER}
                    title={"Select Balance Transfer Case"}
                    okText={"Submit"}
                    confirmLoading={submitting}
                    onOk={() => this.submit("balance_transfer")}
                    onCancel={this.closeModal}
                >
                    {this.renderLimitForm()}
                </Modal>

                <Modal
                    visible={modal === MODALS.UNTAG}
                    title={"Un-Tag Case"}
                    okText={"Proceed"}
                    confirmLoading={submitting}
                    onOk={() => this.submit("untag")}
                    onCancel={this.closeModal}
                />
            </span>
        );
    }
}

FiMovementBar.propTypes = {
    // Every row in the grid — the Tag flow picks the destination case from here.
    rows: PropTypes.array,
    selectedRows: PropTypes.array,
    permissionContext: PropTypes.object,
    recreditViewOnlyPermission: PropTypes.bool,
    // Called after a successful action so the parent can refetch the grid.
    onDone: PropTypes.func
};

export default FiMovementBar;
